/**
 * HealthRecordTab — the primary tab on the ClientDetail page.
 *
 * Renders the longitudinal patient health record: alerts strip at the
 * top, filter/search bar, pinned strip, then the reverse-chronological
 * timeline. Inline dialogs handle:
 *   - creating / editing / deactivating medical alerts
 *   - uploading attachments (multipart → Emergent Object Storage)
 *   - logging communications (phone / in-person / email / SMS / letter)
 *
 * The timeline itself is virtual — the backend aggregates from
 * assessments, soap notes, intake submissions, letters, attachments,
 * communications and alerts. Adding a new record type only requires
 * a couple of lines in the backend service; no schema changes here.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { useCanonicalClient } from "@/lib/canonicalClient";
import { ACCEPT, BulkUploadDialog } from "@/components/files/BulkUploadDialog";
import { FileDropZone } from "@/components/files/FileDropZone";
import { FilePreviewDialog } from "@/components/files/FilePreviewDialog";
import { FileEditDialog } from "@/components/files/FileEditDialog";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import MedicalHistoryPanel from "@/components/MedicalHistoryPanel";
import MedicationsPanel from "@/components/MedicationsPanel";
import AllergiesPanel from "@/components/AllergiesPanel";
import ConsentsPanel from "@/components/ConsentsPanel";
import HandwritingUploadsPanel from "@/components/HandwritingUploadsPanel";
import SbnLinkCard from "@/components/SbnLinkCard";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
  DialogDescription,
} from "@/components/ui/dialog";
import {
  AlertTriangle, Plus, Search, Pin, PinOff, Loader2, FileText, MessageSquare,
  Phone, Paperclip, Upload, Trash2, Download, Printer, Stethoscope, ClipboardCheck,
  Mail, User as UserIcon, Filter, X, ArrowRight, LineChart, Activity, Sparkles,
  Pill, ShieldAlert, HeartPulse, FileSignature, Dumbbell, Link as LinkIcon, Eye, Pencil, Library,
} from "lucide-react";
import { toast } from "sonner";
import { cn } from "@/lib/utils";
import { printPdfBlob } from "@/lib/printPdf";
import OutcomeMeasuresPanel from "@/components/OutcomeMeasuresPanel";
import BodyChartDialog from "@/components/BodyChartDialog";
import TreatmentNoteDialog from "@/components/TreatmentNoteDialog";
import AiSummaryDialog from "@/components/AiSummaryDialog";
import DeleteWithReasonDialog from "@/components/DeleteWithReasonDialog";
import { SendResourceButton } from "@/components/resources/SendResourceDialog";
import { AssignmentRowActions } from "@/components/resources/AssignmentRowActions";

const KIND_META = {
  assessment:    { label: "Assessment",   icon: Stethoscope,    tone: "text-indigo-700 bg-indigo-50 border-indigo-200" },
  soap_note:     { label: "SOAP note",    icon: FileText,       tone: "text-emerald-700 bg-emerald-50 border-emerald-200" },
  intake:        { label: "Intake",       icon: ClipboardCheck, tone: "text-sky-700 bg-sky-50 border-sky-200" },
  letter:        { label: "Letter",       icon: Mail,           tone: "text-amber-700 bg-amber-50 border-amber-200" },
  attachment:    { label: "File",         icon: Paperclip,      tone: "text-zinc-700 bg-zinc-50 border-zinc-200" },
  communication: { label: "Communication",icon: Phone,          tone: "text-purple-700 bg-purple-50 border-purple-200" },
  medical_alert: { label: "Alert",        icon: AlertTriangle,  tone: "text-red-700 bg-red-50 border-red-200" },
  measurement:   { label: "Measure",      icon: LineChart,      tone: "text-cyan-700 bg-cyan-50 border-cyan-200" },
  body_chart:    { label: "Body chart",   icon: Activity,       tone: "text-fuchsia-700 bg-fuchsia-50 border-fuchsia-200" },
  treatment_note:{ label: "Note",         icon: FileText,       tone: "text-teal-700 bg-teal-50 border-teal-200" },
  medication:    { label: "Medication",   icon: Pill,           tone: "text-rose-700 bg-rose-50 border-rose-200" },
  allergy:       { label: "Allergy",      icon: ShieldAlert,    tone: "text-orange-700 bg-orange-50 border-orange-200" },
  medical_history:{ label: "History",     icon: HeartPulse,     tone: "text-violet-700 bg-violet-50 border-violet-200" },
  consent:       { label: "Consent",      icon: FileSignature,  tone: "text-lime-700 bg-lime-50 border-lime-200" },
  exercise_program: { label: "Program",   icon: Dumbbell,       tone: "text-emerald-700 bg-emerald-50 border-emerald-200" },
  resource_assignment: { label: "Resource", icon: Library,      tone: "text-sky-700 bg-sky-50 border-sky-200" },
};

// Kinds that already have their own dedicated tabs on the client
// detail page (Notes, Forms/Onboarding review, Programs). We hide
// them from the Health Record timeline and its filter chips so the
// section stops duplicating what those tabs already show — the
// KIND_META entries stay so any pinned legacy rows still render
// correctly, and so we can flip individual kinds back on later
// via a settings toggle without touching schema.
const HIDDEN_TIMELINE_KINDS = new Set([
  "soap_note",         // → Notes tab
  "intake",            // → Forms / Onboarding review tabs
  "exercise_program",  // → Programs tab
]);

// data-testid anchors on the structured panels so timeline rows can
// scroll the practitioner directly to the source-of-truth section
// when opened. Kept in one place so the panels and the router below
// stay in lock-step.
const PANEL_ANCHOR_BY_KIND = {
  medication: "medications-panel",
  allergy: "allergies-panel",
  medical_history: "medical-history-panel",
  consent: "consents-panel",
  exercise_program: "exercise-programs-panel",
};

const SEVERITY_LABEL = {
  critical: "Critical",
  high: "High priority",
  routine: "Routine precaution",
  admin: "Admin reminder",
  // Legacy — still surfaces if any row wasn't migrated.
  info: "Information", caution: "Caution", significant: "Significant clinical risk",
};
const SEVERITY_TONE = {
  critical: "border-red-400 bg-red-50 text-red-900",
  high: "border-amber-400 bg-amber-50 text-amber-900",
  routine: "border-sky-300 bg-sky-50 text-sky-900",
  admin: "border-zinc-300 bg-zinc-50 text-zinc-700",
  // Legacy aliases (backward-compat during rollout).
  info: "border-sky-300 bg-sky-50 text-sky-900",
  caution: "border-amber-300 bg-amber-50 text-amber-900",
  significant: "border-red-300 bg-red-50 text-red-900",
};

export default function HealthRecordTab({ email, clientName, onOpenTab, onOpenNewAssessment }) {
  const navigate = useNavigate();
  const { can } = useAuth();
  const canArchive = can("owner", "manager");
  const canonical = useCanonicalClient();
  const [alerts, setAlerts] = useState([]);
  const [timeline, setTimeline] = useState([]);
  const [pins, setPins] = useState([]);
  const [loading, setLoading] = useState(true);

  // Filters
  const [selectedKinds, setSelectedKinds] = useState(new Set());  // empty = all
  const [search, setSearch] = useState("");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");

  // Dialogs
  const [showAlert, setShowAlert] = useState(false);
  const [editingAlert, setEditingAlert] = useState(null);
  const [uploadQueue, setUploadQueue] = useState(null);
  const uploadPickRef = useRef(null);
  const [showComm, setShowComm] = useState(false);
  const [showBodyChart, setShowBodyChart] = useState(false);
  const [viewingBodyChart, setViewingBodyChart] = useState(null);
  const [showTreatmentNote, setShowTreatmentNote] = useState(false);
  const [viewingNoteId, setViewingNoteId] = useState(null);
  const [pendingSummaryBlock, setPendingSummaryBlock] = useState(null);
  const [showAiSummary, setShowAiSummary] = useState(false);
  const [aiSummaryStale, setAiSummaryStale] = useState(false);
  const [aiSummaryExists, setAiSummaryExists] = useState(false);
  const [omRefreshKey, setOmRefreshKey] = useState(0);

  const refreshAlerts = useCallback(async () => {
    try {
      const r = await api.get(`/health-record/${encodeURIComponent(email)}/alerts`, {
        params: {
          include_inactive: true,
          // Phase-2 scoped filter — narrow to just this canonical
          // client's alerts when viewing `?as=<cid>`.
          ...(canonical?.clientId ? { canonical_client_id: canonical.clientId } : {}),
        },
      });
      setAlerts(r.data?.alerts || []);
    } catch (e) {
      // Non-fatal — timeline still works.
    }
  }, [email, canonical?.clientId]);

  const refreshTimeline = useCallback(async () => {
    setLoading(true);
    try {
      const kinds = Array.from(selectedKinds).join(",");
      const r = await api.get(`/health-record/${encodeURIComponent(email)}/timeline`, {
        params: {
          kinds: kinds || undefined,
          search: search || undefined,
          from: dateFrom || undefined,
          to: dateTo || undefined,
          ...(canonical?.clientId ? { canonical_client_id: canonical.clientId } : {}),
        },
      });
      // Server returns every kind; the Health Record tab only
      // shows kinds that aren't already covered by a dedicated tab.
      // Filtering here (rather than at the API) keeps the endpoint
      // reusable for other views (e.g. the AI summary generator
      // still wants SOAP notes + intakes in its context).
      const rawItems = r.data?.items || [];
      const visibleItems = rawItems.filter((i) => !HIDDEN_TIMELINE_KINDS.has(i.kind));
      setTimeline(visibleItems);
      setPins(r.data?.pins || []);
    } catch (e) {
      toast.error("Could not load timeline", { description: e?.response?.data?.detail || e.message });
    } finally {
      setLoading(false);
    }
  }, [email, selectedKinds, search, dateFrom, dateTo, canonical?.clientId]);

  useEffect(() => { refreshAlerts(); }, [refreshAlerts]);
  useEffect(() => { refreshTimeline(); }, [refreshTimeline]);

  // Poll AI summary status (existence + staleness) so the top button can
  // show a "Stale — regenerate?" badge without opening the dialog.
  const refreshAiStatus = useCallback(async () => {
    try {
      const r = await api.get(`/health-record/${encodeURIComponent(email)}/ai-summary`);
      setAiSummaryExists(!!r.data?.summary);
      setAiSummaryStale(!!r.data?.stale && !!r.data?.summary);
    } catch {
      // Non-fatal.
    }
  }, [email]);
  useEffect(() => { refreshAiStatus(); }, [refreshAiStatus, timeline.length]);
  useEffect(() => {
    // Row-level deletes fire this so we don't need to prop-drill a
    // refresh callback through every action button.
    const handler = () => { refreshTimeline(); setOmRefreshKey((k) => k + 1); };
    window.addEventListener("hr-refresh", handler);
    return () => window.removeEventListener("hr-refresh", handler);
  }, [refreshTimeline]);

  const activeAlerts = useMemo(() => alerts.filter((a) => a.active), [alerts]);
  const pinnedItems = useMemo(() => timeline.filter((i) => i.pinned), [timeline]);
  const unpinnedItems = useMemo(() => timeline.filter((i) => !i.pinned), [timeline]);

  async function togglePin(item) {
    try {
      await api.post(`/health-record/${encodeURIComponent(email)}/pins`, {
        kind: item.kind, id: item.id, pinned: !item.pinned,
      });
      // Optimistic — flip locally instead of a full refetch.
      setTimeline((rows) => rows.map((r) => (r.id === item.id && r.kind === item.kind ? { ...r, pinned: !r.pinned } : r)));
    } catch (e) {
      toast.error("Could not pin", { description: e?.response?.data?.detail || e.message });
    }
  }

  function toggleKind(k) {
    setSelectedKinds((prev) => {
      const next = new Set(prev);
      if (next.has(k)) next.delete(k);
      else next.add(k);
      return next;
    });
  }

  async function deactivateAlert(a) {
    if (!window.confirm(`Deactivate alert "${a.title}"? It will hide from the header but stay in the record for auditability.`)) return;
    try {
      await api.delete(`/health-record/alerts/${a.id}`);
      refreshAlerts();
      refreshTimeline();
    } catch (e) {
      toast.error("Could not deactivate", { description: e?.response?.data?.detail || e.message });
    }
  }

  return (
    <div className="space-y-4" data-testid="health-record-tab">
      {/* AI Patient Summary — prominent top action */}
      <div
        className="flex items-center justify-between gap-3 flex-wrap p-3 rounded-lg border border-indigo-200 bg-gradient-to-r from-indigo-50 to-white"
        data-testid="hr-ai-summary-strip"
      >
        <div className="flex items-center gap-2">
          <Sparkles className="h-5 w-5 text-indigo-600" />
          <div>
            <div className="text-sm font-semibold text-zinc-950">AI patient summary</div>
            <div className="text-2xs text-zinc-600">
              {aiSummaryExists
                ? (aiSummaryStale
                    ? "New records added since last summary — regenerate for the latest picture."
                    : "One-page current picture + what's changed since last visit.")
                : "Generate a one-page briefing before the next appointment."}
            </div>
          </div>
        </div>
        <div className="flex items-center gap-2">
          {aiSummaryStale && aiSummaryExists && (
            <span
              className="text-2xs uppercase tracking-wider px-1.5 py-0.5 rounded border border-amber-300 bg-amber-50 text-amber-800"
              data-testid="hr-ai-summary-stale-badge"
            >
              Stale
            </span>
          )}
          <Button
            onClick={() => setShowAiSummary(true)}
            className="bg-indigo-700 hover:bg-indigo-800 text-white"
            size="sm"
            data-testid="hr-ai-summary-btn"
          >
            <Sparkles className="h-4 w-4 mr-1.5" />
            {aiSummaryExists ? "View AI summary" : "Generate AI summary"}
          </Button>
        </div>
      </div>

      {/* Search + action row — placed prominently right under the
          AI summary so operators land on the two most-used controls
          (search + upload / body-chart / new-note / new-assessment)
          without scrolling. */}
      <FileDropZone onFiles={setUploadQueue} testId="hr-actions-dropzone" overlayTestId="hr-actions-drop-overlay">
      <Card className="p-3 rounded-lg border border-zinc-200 shadow-none">
        <div className="flex flex-wrap gap-2 items-center">
          <div className="relative flex-1 min-w-[220px]">
            <Search className="h-3.5 w-3.5 absolute left-2 top-1/2 -translate-y-1/2 text-zinc-400" />
            <Input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search this health record — pain, medication, referral…"
              className="pl-7 h-9"
              data-testid="hr-search"
            />
          </div>
          <Input
            type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)}
            className="h-9 w-[145px]" placeholder="From"
            data-testid="hr-date-from"
          />
          <Input
            type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)}
            className="h-9 w-[145px]" placeholder="To"
            data-testid="hr-date-to"
          />
          {(search || dateFrom || dateTo || selectedKinds.size > 0) && (
            <Button
              size="sm" variant="ghost"
              onClick={() => { setSearch(""); setDateFrom(""); setDateTo(""); setSelectedKinds(new Set()); }}
              data-testid="hr-clear-filters"
            >
              <X className="h-3.5 w-3.5 mr-1" /> Clear
            </Button>
          )}
        </div>
        <div className="mt-2 flex flex-wrap gap-1.5">
          <span className="text-2xs uppercase tracking-wider text-zinc-500 mr-1 py-1 flex items-center gap-1">
            <Filter className="h-3 w-3" /> Type
          </span>
          {Object.entries(KIND_META)
            .filter(([k]) => !HIDDEN_TIMELINE_KINDS.has(k))
            .map(([k, m]) => {
            const active = selectedKinds.has(k);
            const Icon = m.icon;
            return (
              <button
                key={k}
                onClick={() => toggleKind(k)}
                data-testid={`hr-filter-${k}`}
                className={cn(
                  "text-2xs px-2 py-1 rounded-full border inline-flex items-center gap-1 transition-colors",
                  active
                    ? "bg-zinc-950 text-white border-zinc-950"
                    : "bg-white text-zinc-600 border-zinc-200 hover:border-zinc-400",
                )}
              >
                <Icon className="h-3 w-3" /> {m.label}
              </button>
            );
          })}
        </div>
        <div className="mt-3 pt-3 border-t border-zinc-100 flex flex-wrap gap-2">
          <Button size="sm" variant="outline" onClick={() => uploadPickRef.current?.click()} data-testid="hr-upload-btn">
            <Upload className="h-3.5 w-3.5 mr-1.5" /> Upload files
          </Button>
          <input ref={uploadPickRef} type="file" multiple accept={ACCEPT} className="hidden" data-testid="hr-upload-input"
                 onChange={(e) => { const fs = Array.from(e.target.files || []); e.target.value = ""; if (fs.length) setUploadQueue(fs); }} />
          <SendResourceButton client={{ email, name: clientName, canonicalId: canonical?.clientId || null }}
                              source={{ kind: "client_record" }} testId="hr-send-resource-btn" />
          <Button size="sm" variant="outline" onClick={() => setShowComm(true)} data-testid="hr-log-comm-btn">
            <MessageSquare className="h-3.5 w-3.5 mr-1.5" /> Log communication
          </Button>
          <Button size="sm" variant="outline" onClick={() => setShowBodyChart(true)} data-testid="hr-body-chart-btn">
            <Activity className="h-3.5 w-3.5 mr-1.5" /> Body chart
          </Button>
          <Button size="sm" variant="outline" onClick={() => setShowTreatmentNote(true)} data-testid="hr-treatment-note-btn">
            <FileText className="h-3.5 w-3.5 mr-1.5" /> New note
          </Button>
          <Button
            size="sm"
            variant="outline"
            onClick={() => {
              // Preferred path: parent (ClientDetail) exposes a single
              // callback that both switches to the Assessments tab and
              // signals the dialog to open with the client details
              // pre-filled — one click, no page navigation.
              if (typeof onOpenNewAssessment === "function") {
                onOpenNewAssessment();
                return;
              }
              // Fallback: navigate to the standalone Assessments page
              // with query params (used when this tab is rendered
              // outside of ClientDetail).
              const params = new URLSearchParams();
              params.set("new", "1");
              if (email) params.set("client_email", email);
              if (clientName) params.set("client_name", clientName);
              navigate(`/assessments?${params.toString()}`);
            }}
            data-testid="hr-new-assessment-btn"
          >
            <Stethoscope className="h-3.5 w-3.5 mr-1.5" /> New assessment
          </Button>
          <span className="hidden md:inline self-center ml-auto text-2xs text-zinc-400">or drag files here to upload</span>
        </div>
      </Card>
      </FileDropZone>

      {/* Medical alerts strip */}
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="text-xs uppercase tracking-wider text-zinc-500 font-semibold flex items-center gap-1.5">
          <AlertTriangle className="h-3 w-3" /> Medical alerts
        </div>
        <Button
          size="sm" variant="outline"
          onClick={() => { setEditingAlert(null); setShowAlert(true); }}
          data-testid="hr-add-alert-btn"
        >
          <Plus className="h-3.5 w-3.5 mr-1.5" /> Add alert
        </Button>
      </div>
      {activeAlerts.length === 0 ? (
        <div className="text-xs text-zinc-500 italic border border-dashed border-zinc-200 rounded-md p-3">
          No medical alerts on file. Add one when a clinical risk warrants pre-consult attention (falls,
          anticoagulant, pacemaker, latex allergy, etc.).
        </div>
      ) : (
        <div className="flex flex-wrap gap-2" data-testid="hr-alerts-strip">
          {activeAlerts.map((a) => (
            <button
              key={a.id}
              onClick={() => { setEditingAlert(a); setShowAlert(true); }}
              className={cn(
                "text-left group relative inline-flex flex-col rounded-md border px-3 py-2 max-w-md transition-shadow hover:shadow-sm",
                SEVERITY_TONE[a.severity] || SEVERITY_TONE.routine,
              )}
              data-testid={`hr-alert-${a.id}`}
            >
              <div className="flex items-center gap-1.5 text-xs font-semibold">
                <AlertTriangle className="h-3 w-3" />
                {a.title}
                <span className="ml-1 text-2xs font-normal opacity-75">· {SEVERITY_LABEL[a.severity] || a.severity}</span>
              </div>
              {a.description && (
                <div className="text-2xs mt-0.5 opacity-90 line-clamp-2">{a.description}</div>
              )}
            </button>
          ))}
        </div>
      )}

      {/* Outcome measures — trend graphs */}
      <OutcomeMeasuresPanel email={email} refreshKey={omRefreshKey} />

      {/* Strength by Numbers link — surface / auto-suggest inline so
          the clinician doesn't need to swap tabs. Full session detail
          still lives on the SbN tab. */}
      <SbnLinkCard
        email={email}
        clientName={clientName}
        onOpenSbnTab={onOpenTab ? () => onOpenTab("sbn") : null}
      />

      {/* Structured medical history — conditions, surgery, falls */}
      <MedicalHistoryPanel email={email} />

      {/* Medications register */}
      <MedicationsPanel email={email} />

      {/* Allergies & sensitivities */}
      <AllergiesPanel email={email} />

      {/* Consents (spec §19) */}
      <ConsentsPanel email={email} />

      {/* Handwriting recognition (Stage 1) — upload & review handwritten
          intake forms / assessment notes; produces structured blocks
          the practitioner reviews before saving to the record. */}
      <HandwritingUploadsPanel email={email} onApproved={refreshTimeline} />

      <FileDropZone onFiles={setUploadQueue} className="space-y-1.5"
                    testId="hr-timeline-dropzone" overlayTestId="hr-timeline-drop-overlay">
      {/* Pinned strip */}
      {pinnedItems.length > 0 && (
        <div className="space-y-1.5">
          <div className="text-2xs uppercase tracking-wider text-zinc-500 font-semibold flex items-center gap-1.5">
            <Pin className="h-3 w-3" /> Pinned
          </div>
          <div className="space-y-1.5">
            {pinnedItems.map((it) => (
              <TimelineRow
                key={`${it.kind}:${it.id}`}
                item={it}
                onPin={togglePin}
                onDeactivateAlert={deactivateAlert}
                onOpenBodyChart={setViewingBodyChart}
                onOpenNote={setViewingNoteId}
                onOmRefresh={() => setOmRefreshKey((k) => k + 1)}
                onRefresh={refreshTimeline}
              />
            ))}
          </div>
        </div>
      )}

      {/* Timeline */}
      <div className="space-y-1.5">
        {pinnedItems.length > 0 && (
          <div className="text-2xs uppercase tracking-wider text-zinc-500 font-semibold mt-2">
            All records
          </div>
        )}
        {loading ? (
          <div className="text-sm text-zinc-500 flex items-center gap-2 p-6">
            <Loader2 className="h-4 w-4 animate-spin" /> Loading timeline…
          </div>
        ) : unpinnedItems.length === 0 && pinnedItems.length === 0 ? (
          <div className="text-sm text-zinc-500 italic border border-dashed border-zinc-200 rounded-md p-8 text-center">
            No records for the current filters. Try clearing filters or add a new record above.
          </div>
        ) : (
          unpinnedItems.map((it) => (
            <TimelineRow
              key={`${it.kind}:${it.id}`}
              item={it}
              onPin={togglePin}
              onDeactivateAlert={deactivateAlert}
              onOpenBodyChart={setViewingBodyChart}
              onOpenNote={setViewingNoteId}
              onOmRefresh={() => setOmRefreshKey((k) => k + 1)}
              onRefresh={refreshTimeline}
            />
          ))
        )}
      </div>
      </FileDropZone>

      {/* Dialogs */}
      {showAlert && (
        <AlertDialog
          email={email}
          existing={editingAlert}
          onClose={() => { setShowAlert(false); setEditingAlert(null); }}
          onSaved={() => { refreshAlerts(); refreshTimeline(); }}
          onDeactivate={deactivateAlert}
          canArchive={canArchive}
        />
      )}
      {uploadQueue && (
        <BulkUploadDialog
          email={email}
          initialFiles={uploadQueue}
          onClose={() => setUploadQueue(null)}
          onUploaded={refreshTimeline}
        />
      )}
      {showComm && (
        <CommunicationDialog
          email={email}
          onClose={() => setShowComm(false)}
          onSaved={() => { setShowComm(false); refreshTimeline(); }}
        />
      )}
      {showBodyChart && (
        <BodyChartDialog
          email={email}
          existing={null}
          onClose={() => setShowBodyChart(false)}
          onSaved={() => { setShowBodyChart(false); refreshTimeline(); }}
        />
      )}
      {viewingBodyChart && (
        <BodyChartDialog
          email={email}
          existing={viewingBodyChart}
          onClose={() => setViewingBodyChart(null)}
          onSaved={() => setViewingBodyChart(null)}
        />
      )}
      {(showTreatmentNote || viewingNoteId) && (
        <TreatmentNoteDialog
          email={email}
          noteId={viewingNoteId}
          initialSummaryBlock={pendingSummaryBlock}
          onClose={() => { setShowTreatmentNote(false); setViewingNoteId(null); setPendingSummaryBlock(null); }}
          onSaved={() => { setShowTreatmentNote(false); setViewingNoteId(null); setPendingSummaryBlock(null); refreshTimeline(); }}
        />
      )}
      {showAiSummary && (
        <AiSummaryDialog
          email={email}
          onClose={() => { setShowAiSummary(false); refreshAiStatus(); }}
          onInsertIntoNote={(text) => {
            setPendingSummaryBlock(text || "");
            setShowAiSummary(false);
            setShowTreatmentNote(true);
          }}
        />
      )}
    </div>
  );
}


// ---------------------------------------------------------------------------
// Timeline row
// ---------------------------------------------------------------------------

const fileFromItem = (item) => ({
  id: item.id,
  filename: item.meta?.filename || item.title.replace(/^File · /, ""),
  category: item.meta?.category || item.status,
  description: item.meta?.description || "",
  document_date: item.meta?.document_date || null,
  created_at: item.meta?.created_at,
  content_type: item.meta?.content_type,
  size: item.meta?.size,
});

function TimelineRow({ item, onPin, onDeactivateAlert, onOpenBodyChart, onOpenNote, onOmRefresh, onRefresh }) {
  const navigate = useNavigate();
  const { can } = useAuth();
  const isAdmin = can("owner", "manager");
  const [showAttachmentDelete, setShowAttachmentDelete] = useState(false);
  const [fileDialog, setFileDialog] = useState(null);
  const meta = KIND_META[item.kind] || KIND_META.attachment;
  const Icon = meta.icon;

  function openItem() {
    switch (item.kind) {
      case "assessment":
        return navigate(`/assessments/${item.id}`);
      case "soap_note":
      case "letter":
        // These already have their own tabs on the client page — no-op here
        // beyond a toast so the practitioner isn't surprised.
        return toast.info(`${meta.label} — open via the ${item.kind === "letter" ? "Letters" : "Notes / Forms"} tab.`);
      case "intake":
        return item.status === "pending" ? copyIntakeLink() : downloadIntakePdf();
      case "medical_alert":
        return onDeactivateAlert?.(item);
      case "body_chart":
        return onOpenBodyChart?.(item);
      case "treatment_note":
        return onOpenNote?.(item.id);
      case "medication":
      case "allergy":
      case "medical_history":
      case "consent":
      case "exercise_program": {
        // Structured PHR items — scroll to the corresponding panel
        // where the practitioner can view/edit the source-of-truth row.
        const anchor = PANEL_ANCHOR_BY_KIND[item.kind];
        if (!anchor) return null;
        const el = document.querySelector(`[data-testid="${anchor}"]`);
        if (el) {
          el.scrollIntoView({ behavior: "smooth", block: "start" });
          // Brief highlight so the user's eye lands in the right spot.
          el.classList.add("ring-2", "ring-indigo-400", "ring-offset-2");
          setTimeout(() => el.classList.remove("ring-2", "ring-indigo-400", "ring-offset-2"), 1400);
        } else {
          toast.info(`${meta.label} — see the ${meta.label} panel above.`);
        }
        return null;
      }
      default:
        return null;
    }
  }

  async function downloadAttachment() {
    try {
      const r = await api.get(`/health-record/attachments/${item.id}/download`, { responseType: "blob" });
      const blob = new Blob([r.data], { type: item.meta?.content_type || "application/octet-stream" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = item.title.replace(/^File · /, "") || "attachment";
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
    } catch (e) {
      toast.error("Download failed", { description: e?.response?.data?.detail || e.message });
    }
  }

  // Attachments are the only file surface where "Print" is worth
  // wiring up per-row — everything else (SOAP, letter, intake) has a
  // richer dedicated viewer with its own Print action.
  async function printAttachment() {
    const ct = item.meta?.content_type || "";
    // The invisible-iframe print trick only reliably works for PDFs.
    // For other formats (docx, images) we fall back to the download
    // path so the operator can print from their OS viewer.
    if (!ct.includes("pdf")) {
      toast.info("Only PDF attachments print in-app — downloading so you can print from your reader.");
      return downloadAttachment();
    }
    try {
      const r = await api.get(`/health-record/attachments/${item.id}/download`, { responseType: "blob" });
      await printPdfBlob(new Blob([r.data], { type: "application/pdf" }));
    } catch (e) {
      toast.error("Print failed", { description: e?.response?.data?.detail || e.message });
    }
  }

  async function downloadIntakePdf() {
    // Timeline row for an intake carries the submission token in
    // ``meta.token`` (see health_record.service.build_timeline). Pull
    // the PDF straight from the intake API so the practitioner can read
    // the completed onboarding form without leaving the Health Record.
    const token = item?.meta?.token || item?.id;
    if (!token) {
      toast.error("Onboarding record has no PDF token — try the Forms tab.");
      return;
    }
    try {
      const r = await api.get(`/intake/submissions/${token}/pdf`, { responseType: "blob" });
      const blob = new Blob([r.data], { type: "application/pdf" });
      const url = URL.createObjectURL(blob);
      window.open(url, "_blank", "noopener");
      setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch (e) {
      toast.error("Could not open onboarding PDF", {
        description: e?.response?.data?.detail || e.message,
      });
    }
  }

  // Actions for a pending (sent-but-unfilled) intake row.
  async function copyIntakeLink() {
    const token = item?.meta?.token || item?.id;
    if (!token) return;
    // Compute the public intake URL from the current origin so it
    // works in preview / prod without an env round-trip.
    const link = `${window.location.origin}/intake/${token}`;
    try {
      await navigator.clipboard.writeText(link);
      toast.success("Intake link copied to clipboard", { description: link });
    } catch (_e) {
      // Rare Safari fallback: show the link in a toast so the
      // operator can copy manually.
      toast.info("Copy this link", { description: link });
    }
  }

  async function resendIntakeInvite() {
    const token = item?.meta?.token || item?.id;
    if (!token) return;
    try {
      await api.post(`/intake/submissions/${token}/resend`);
      toast.success("Onboarding email re-sent");
      window.dispatchEvent(new CustomEvent("hr-refresh"));
    } catch (e) {
      toast.error("Resend failed", { description: e?.response?.data?.detail || e.message });
    }
  }


  async function deleteAttachment(reason) {
    // Called by the DeleteWithReasonDialog after the admin types a
    // reason. The dialog handles its own errors + closes itself on
    // resolve, so we just perform the request and let it throw.
    await api.delete(`/health-record/attachments/${item.id}`, {
      data: { reason },
    });
    window.dispatchEvent(new CustomEvent("hr-refresh"));
    toast.success("File deleted");
  }

  async function deleteCommunication() {
    if (!window.confirm("Delete this communication log entry?")) return;
    try {
      await api.delete(`/health-record/communications/${item.id}`);
      window.dispatchEvent(new CustomEvent("hr-refresh"));
    } catch (e) {
      toast.error("Delete failed", { description: e?.response?.data?.detail || e.message });
    }
  }

  async function deleteBodyChart() {
    if (!window.confirm("Delete this body chart?")) return;
    try {
      await api.delete(`/health-record/body-charts/${item.id}`);
      window.dispatchEvent(new CustomEvent("hr-refresh"));
    } catch (e) {
      toast.error("Delete failed", { description: e?.response?.data?.detail || e.message });
    }
  }

  async function deleteMeasurement() {
    if (!window.confirm("Delete this measurement?")) return;
    try {
      await api.delete(`/health-record/measurements/${item.id}`);
      onOmRefresh?.();
      window.dispatchEvent(new CustomEvent("hr-refresh"));
    } catch (e) {
      toast.error("Delete failed", { description: e?.response?.data?.detail || e.message });
    }
  }

  async function deleteTreatmentNote() {
    if (!window.confirm("Delete this treatment note? This can't be undone.")) return;
    try {
      await api.delete(`/health-record/notes/${item.id}`);
      window.dispatchEvent(new CustomEvent("hr-refresh"));
    } catch (e) {
      toast.error("Delete failed", { description: e?.response?.data?.detail || e.message });
    }
  }

  return (
    <>
    <Card className="p-3 rounded-lg border border-zinc-200 shadow-none hover:border-zinc-300 transition-colors" data-testid={`hr-row-${item.kind}-${item.id}`}>
      <div className="flex items-start gap-3">
        <div className={cn("h-8 w-8 rounded-md border grid place-items-center shrink-0", meta.tone)}>
          <Icon className="h-4 w-4" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-sm font-medium text-zinc-950 truncate">{item.title}</span>
            {item.status && (
              <span className="text-2xs uppercase tracking-wider px-1.5 py-0.5 rounded border border-zinc-200 bg-zinc-50 text-zinc-600">
                {String(item.status).replace(/_/g, " ")}
              </span>
            )}
          </div>
          {item.summary && (
            <div className="text-xs text-zinc-600 mt-0.5 line-clamp-2">{item.summary}</div>
          )}
          <div className="text-2xs text-zinc-500 mt-1 flex items-center gap-2 flex-wrap font-mono-num">
            <span>{fmtWhen(item.occurred_at)}</span>
            {item.practitioner_name && (<><span>·</span><span className="inline-flex items-center gap-1"><UserIcon className="h-2.5 w-2.5" />{item.practitioner_name}</span></>)}
          </div>
        </div>
        <div className="flex items-center gap-1 shrink-0">
          <button
            onClick={() => onPin?.(item)}
            className="p-1.5 rounded-md hover:bg-zinc-100 text-zinc-500"
            title={item.pinned ? "Unpin" : "Pin to top"}
            data-testid={`hr-pin-${item.kind}-${item.id}`}
          >
            {item.pinned ? <PinOff className="h-3.5 w-3.5" /> : <Pin className="h-3.5 w-3.5" />}
          </button>
          {item.kind === "attachment" && (
            <>
              <button onClick={() => setFileDialog("view")} className="p-1.5 rounded-md hover:bg-zinc-100 text-zinc-500"
                      title="View" data-testid={`hr-view-attachment-${item.id}`}>
                <Eye className="h-3.5 w-3.5" />
              </button>
              <button onClick={() => setFileDialog("edit")} className="p-1.5 rounded-md hover:bg-zinc-100 text-zinc-500"
                      title="Edit details" data-testid={`hr-edit-attachment-${item.id}`}>
                <Pencil className="h-3.5 w-3.5" />
              </button>
              <button onClick={downloadAttachment} className="p-1.5 rounded-md hover:bg-zinc-100 text-zinc-500" title="Download">
                <Download className="h-3.5 w-3.5" />
              </button>
              <button
                onClick={printAttachment}
                className="p-1.5 rounded-md hover:bg-zinc-100 text-zinc-500"
                title="Print"
                data-testid={`hr-print-attachment-${item.id}`}
              >
                <Printer className="h-3.5 w-3.5" />
              </button>
              {isAdmin && (
                <button
                  onClick={() => setShowAttachmentDelete(true)}
                  className="p-1.5 rounded-md hover:bg-red-50 text-red-600"
                  title="Delete"
                  data-testid={`hr-delete-attachment-${item.id}`}
                >
                  <Trash2 className="h-3.5 w-3.5" />
                </button>
              )}
            </>
          )}
          {item.kind === "resource_assignment" && <AssignmentRowActions item={item} canAct={!can("auditor")} />}
          {item.kind === "communication" && (
            <button onClick={deleteCommunication} className="p-1.5 rounded-md hover:bg-red-50 text-red-600" title="Delete">
              <Trash2 className="h-3.5 w-3.5" />
            </button>
          )}
          {item.kind === "body_chart" && (
            <button onClick={deleteBodyChart} className="p-1.5 rounded-md hover:bg-red-50 text-red-600" title="Delete">
              <Trash2 className="h-3.5 w-3.5" />
            </button>
          )}
          {item.kind === "measurement" && (
            <button onClick={deleteMeasurement} className="p-1.5 rounded-md hover:bg-red-50 text-red-600" title="Delete">
              <Trash2 className="h-3.5 w-3.5" />
            </button>
          )}
          {item.kind === "treatment_note" && (
            <button onClick={deleteTreatmentNote} className="p-1.5 rounded-md hover:bg-red-50 text-red-600" title="Delete">
              <Trash2 className="h-3.5 w-3.5" />
            </button>
          )}
          {item.kind === "intake" && item.status === "pending" && (
            <>
              <button
                onClick={copyIntakeLink}
                className="p-1.5 rounded-md hover:bg-zinc-100 text-zinc-500"
                title="Copy intake link"
                data-testid={`hr-intake-copylink-${item.id}`}
              >
                <LinkIcon className="h-3.5 w-3.5" />
              </button>
              <button
                onClick={resendIntakeInvite}
                className="p-1.5 rounded-md hover:bg-zinc-100 text-zinc-500"
                title="Resend intake email"
                data-testid={`hr-intake-resend-${item.id}`}
              >
                <Mail className="h-3.5 w-3.5" />
              </button>
            </>
          )}
          {item.actions?.includes("view") && !["attachment", "communication", "resource_assignment"].includes(item.kind) && (
            <button onClick={openItem} className="p-1.5 rounded-md hover:bg-zinc-100 text-zinc-500" title="Open">
              <ArrowRight className="h-3.5 w-3.5" />
            </button>
          )}
        </div>
      </div>
    </Card>
    {item.kind === "attachment" && (
      <DeleteWithReasonDialog
        open={showAttachmentDelete}
        onOpenChange={setShowAttachmentDelete}
        title="Delete this document?"
        description="The document will be hidden from this client's timeline. An admin can restore it later from Deleted Items."
        itemLabel={`${item.title || "Attachment"}`}
        confirmLabel="Delete document"
        onConfirm={deleteAttachment}
        testidPrefix={`hr-attachment-delete-${item.id}`}
      />
    )}
    {fileDialog === "view" && <FilePreviewDialog file={fileFromItem(item)} onClose={() => setFileDialog(null)} />}
    {fileDialog === "edit" && (
      <FileEditDialog file={fileFromItem(item)} onClose={() => setFileDialog(null)}
                      onSaved={() => { setFileDialog(null); onRefresh?.(); }} />
    )}
  </>
  );
}
// ---------------------------------------------------------------------------

function AlertDialog({ email, existing, onClose, onSaved, onDeactivate, canArchive }) {
  const canonical = useCanonicalClient();
  const [title, setTitle] = useState(existing?.title || "");
  const [severity, setSeverity] = useState(existing?.severity || "high");
  const [description, setDescription] = useState(existing?.description || "");
  const [reviewDate, setReviewDate] = useState(existing?.review_date || "");
  const [saving, setSaving] = useState(false);

  async function save() {
    if (!title.trim()) { toast.error("Title required"); return; }
    setSaving(true);
    try {
      const body = {
        title, severity, description, review_date: reviewDate || null,
        // Phase-2 scoped writes — new alert appears only on this
        // canonical page (Judy vs Rosemarie).
        canonical_client_id: canonical?.clientId || null,
      };
      if (existing?.id) {
        await api.patch(`/health-record/alerts/${existing.id}`, body);
      } else {
        await api.post(`/health-record/${encodeURIComponent(email)}/alerts`, body);
      }
      toast.success(existing ? "Alert updated" : "Alert added");
      onSaved?.();
      onClose();
    } catch (e) {
      toast.error("Save failed", { description: e?.response?.data?.detail || e.message });
    } finally {
      setSaving(false);
    }
  }

  return (
    <Dialog open onOpenChange={(v) => !v && onClose()}>
      <DialogContent data-testid="hr-alert-dialog">
        <DialogHeader>
          <DialogTitle>{existing ? "Edit medical alert" : "Add medical alert"}</DialogTitle>
          <DialogDescription>
            Alerts show as a coloured chip in the client header on every clinical screen so
            practitioners see them before starting a consultation. Critical alerts also
            surface on the Appointments detail screen.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div>
            <Label className="text-xs">Title</Label>
            <Input
              value={title} onChange={(e) => setTitle(e.target.value)}
              placeholder="e.g. Falls Risk, Anticoagulant, Pacemaker, Latex Allergy"
              data-testid="hr-alert-title"
            />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-xs">Severity</Label>
              <select
                value={severity}
                onChange={(e) => setSeverity(e.target.value)}
                className="mt-1 w-full h-9 px-2 border border-zinc-300 rounded-md text-sm bg-white"
                data-testid="hr-alert-severity"
              >
                <option value="critical">Critical (blocks safe treatment)</option>
                <option value="high">High priority</option>
                <option value="routine">Routine precaution</option>
                <option value="admin">Administrative reminder</option>
              </select>
            </div>
            <div>
              <Label className="text-xs">Review date (optional)</Label>
              <Input
                type="date"
                value={reviewDate || ""}
                onChange={(e) => setReviewDate(e.target.value)}
                className="mt-1"
                data-testid="hr-alert-review-date"
              />
            </div>
          </div>
          <div>
            <Label className="text-xs">Description (optional)</Label>
            <Textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              rows={3}
              placeholder="Any relevant context — e.g. 3 falls in past 6 months, on Warfarin"
              data-testid="hr-alert-description"
            />
          </div>
          {existing?.acknowledgements?.length > 0 && (
            <div className="text-2xs text-zinc-600 bg-zinc-50 border border-zinc-200 rounded-md p-2 space-y-0.5" data-testid="hr-alert-ack-list">
              <div className="uppercase tracking-wider text-zinc-400 text-2xs">Acknowledged by</div>
              {existing.acknowledgements.map((a, i) => (
                <div key={i} className="flex items-center justify-between gap-2">
                  <span>{a.by_name || a.by_id}</span>
                  <span className="text-zinc-500">{a.at ? new Date(a.at).toLocaleString() : ""}</span>
                </div>
              ))}
            </div>
          )}
        </div>
        <DialogFooter className="gap-2 sm:justify-between">
          <div>
            {existing?.id && canArchive && (
              <Button
                variant="outline"
                className="text-red-700 border-red-300 hover:bg-red-50"
                onClick={() => { onDeactivate?.(existing); onClose(); }}
                data-testid="hr-alert-deactivate"
              >
                Archive
              </Button>
            )}
          </div>
          <div className="flex gap-2">
            <Button variant="outline" onClick={onClose} disabled={saving}>Cancel</Button>
            <Button onClick={save} disabled={saving} data-testid="hr-alert-save">
              {saving ? "Saving…" : (existing ? "Save changes" : "Add alert")}
            </Button>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}


// ---------------------------------------------------------------------------
// CommunicationDialog — log a phone call / email / in-person chat
// ---------------------------------------------------------------------------

function CommunicationDialog({ email, onClose, onSaved }) {
  const [method, setMethod] = useState("phone_call");
  const [subject, setSubject] = useState("");
  const [notes, setNotes] = useState("");
  const [participants, setParticipants] = useState("");
  const [followUp, setFollowUp] = useState(false);
  const [followUpDate, setFollowUpDate] = useState("");
  const [occurredAt, setOccurredAt] = useState("");
  const [saving, setSaving] = useState(false);

  async function save() {
    setSaving(true);
    try {
      await api.post(`/health-record/${encodeURIComponent(email)}/communications`, {
        method,
        subject,
        notes,
        participants: participants.split(",").map((s) => s.trim()).filter(Boolean),
        occurred_at: occurredAt ? new Date(occurredAt).toISOString() : undefined,
        follow_up_required: followUp,
        follow_up_date: followUp ? followUpDate || null : null,
      });
      toast.success("Communication logged");
      onSaved?.();
    } catch (e) {
      toast.error("Save failed", { description: e?.response?.data?.detail || e.message });
    } finally {
      setSaving(false);
    }
  }

  return (
    <Dialog open onOpenChange={(v) => !v && onClose()}>
      <DialogContent data-testid="hr-comm-dialog">
        <DialogHeader>
          <DialogTitle>Log communication</DialogTitle>
          <DialogDescription>
            Record a phone call, in-person chat, email, SMS or letter against this client so it
            joins the longitudinal record.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-xs">Method</Label>
              <select
                value={method} onChange={(e) => setMethod(e.target.value)}
                className="mt-1 w-full h-9 px-2 border border-zinc-300 rounded-md text-sm bg-white"
                data-testid="hr-comm-method"
              >
                <option value="phone_call">Phone call</option>
                <option value="in_person">In-person</option>
                <option value="email">Email</option>
                <option value="sms">SMS</option>
                <option value="letter">Letter</option>
                <option value="other">Other</option>
              </select>
            </div>
            <div>
              <Label className="text-xs">Occurred at (optional)</Label>
              <Input
                type="datetime-local" value={occurredAt}
                onChange={(e) => setOccurredAt(e.target.value)}
                data-testid="hr-comm-occurred-at"
              />
            </div>
          </div>
          <div>
            <Label className="text-xs">Subject</Label>
            <Input
              value={subject} onChange={(e) => setSubject(e.target.value)}
              placeholder="e.g. Follow-up re. seizure activity"
              data-testid="hr-comm-subject"
            />
          </div>
          <div>
            <Label className="text-xs">Participants (comma-separated)</Label>
            <Input
              value={participants} onChange={(e) => setParticipants(e.target.value)}
              placeholder="Dr Smith, Jane Smith"
              data-testid="hr-comm-participants"
            />
          </div>
          <div>
            <Label className="text-xs">Notes</Label>
            <Textarea
              value={notes} onChange={(e) => setNotes(e.target.value)}
              rows={4}
              placeholder="What was discussed and agreed?"
              data-testid="hr-comm-notes"
            />
          </div>
          <div className="flex items-center gap-2 text-sm">
            <input
              type="checkbox" checked={followUp} onChange={(e) => setFollowUp(e.target.checked)}
              className="h-4 w-4"
              data-testid="hr-comm-follow-up"
            />
            <Label className="text-xs">Follow-up required</Label>
            {followUp && (
              <Input
                type="date" value={followUpDate}
                onChange={(e) => setFollowUpDate(e.target.value)}
                className="h-8 w-40 ml-2"
                data-testid="hr-comm-follow-up-date"
              />
            )}
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={saving}>Cancel</Button>
          <Button onClick={save} disabled={saving} data-testid="hr-comm-save">
            {saving ? "Saving…" : "Save"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}


// ---------------------------------------------------------------------------
// helpers
// ---------------------------------------------------------------------------

function fmtWhen(iso) {
  if (!iso) return "";
  try {
    const d = new Date(iso);
    return d.toLocaleString("en-AU", { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" });
  } catch { return iso; }
}
